import React, { createContext, useContext, useMemo } from 'react';
import { useSmartFetch, CPLAT_API_ROUTES } from '@/api';
import { useAppSelector } from '@/redux/store';
import type {
  DiagnosisListItem,
  DiagnosisListResponse,
  DiagnosisUsage,
  DiagnosisUsageResponse,
} from '@/types/productOptimization';

type Ctx = {
  diagnoses: DiagnosisListItem[];
  usage: DiagnosisUsage | null;
  isLoading: boolean;
  isError: boolean;
  refetchList: () => Promise<unknown>;
  refetchUsage: () => Promise<unknown>;
};

const ProductOptimizationContext = createContext<Ctx | undefined>(undefined);

export const ProductOptimizationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const cplatToken = useAppSelector((state) => state.user.CplatToken);

  const {
    data: listData,
    isLoading: listLoading,
    isError: listError,
    refetch: refetchList,
  } = useSmartFetch<DiagnosisListResponse>(CPLAT_API_ROUTES.productOptimizationList, {
    method: 'POST',
    params: { cplatToken },
    v3: true,
    noSuspense: true,
    config: {
      refetchOnWindowFocus: true,
      refetchInterval: (query: { state: { data?: DiagnosisListResponse } }) => {
        const hasAnalyzing = (query.state.data?.data ?? []).some(
          (d) => d.status === 'Analyzing',
        );
        return hasAnalyzing ? 10_000 : false;
      },
    } as any,
  });

  const {
    data: usageData,
    isLoading: usageLoading,
    isError: usageError,
    refetch: refetchUsage,
  } = useSmartFetch<DiagnosisUsageResponse>(CPLAT_API_ROUTES.productOptimizationUsage, {
    method: 'POST',
    params: { cplatToken },
    v3: true,
    noSuspense: true,
  });

  const diagnoses = useMemo(() => listData?.data ?? [], [listData]);
  const usage = useMemo(() => usageData?.data?.[0] ?? null, [usageData]);

  const value = useMemo(
    () => ({
      diagnoses,
      usage,
      isLoading: listLoading || usageLoading,
      isError: listError || usageError,
      refetchList,
      refetchUsage,
    }),
    [diagnoses, usage, listLoading, usageLoading, listError, usageError, refetchList, refetchUsage],
  );

  return (
    <ProductOptimizationContext.Provider value={value}>
      {children}
    </ProductOptimizationContext.Provider>
  );
};

export const useProductOptimization = () => {
  const ctx = useContext(ProductOptimizationContext);
  if (!ctx) {
    throw new Error(
      'useProductOptimization must be used within ProductOptimizationProvider',
    );
  }
  return ctx;
};
